import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Search, Briefcase, Building2, FileText, User, Compass, Loader2 } from "lucide-react";

// Iter-87: compact global search bar in the app header. Debounced (180ms) lookup against
// GET /api/search?q=…. Shows up to 10 results grouped by kind, keyboard-navigable
// (ArrowUp/Down to move, Enter to open, Esc to dismiss). Click = navigate.

const KIND_ICON = {
  mission: Briefcase,
  client: Building2,
  invoice: FileText,
  user: User,
  page: Compass,
};

const KIND_LABEL = {
  mission: "Mission",
  client: "Client",
  invoice: "Invoice",
  user: "User",
  page: "Page",
};

export default function GlobalSearch() {
  const [q, setQ] = useState("");
  const [hits, setHits] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const navigate = useNavigate();
  const rootRef = useRef(null);
  const inputRef = useRef(null);

  // Debounced fetch.
  useEffect(() => {
    if (!q || q.trim().length < 2) { setHits([]); setLoading(false); return; }
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/search?q=${encodeURIComponent(q.trim())}`);
        setHits(Array.isArray(data?.hits) ? data.hits : []);
        setActiveIdx(0);
      } catch {
        setHits([]);
      } finally {
        setLoading(false);
      }
    }, 180);
    return () => clearTimeout(t);
  }, [q]);

  // Close on outside click.
  useEffect(() => {
    const onDocClick = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  // Global ⌘K / Ctrl+K focus shortcut.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const pick = (hit) => {
    if (!hit) return;
    setOpen(false);
    setQ("");
    setHits([]);
    navigate(hit.link);
  };

  const onKey = (e) => {
    if (!open && e.key !== "Escape") setOpen(true);
    if (e.key === "ArrowDown") { e.preventDefault(); setActiveIdx((i) => Math.min(hits.length - 1, i + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActiveIdx((i) => Math.max(0, i - 1)); }
    else if (e.key === "Enter") { e.preventDefault(); pick(hits[activeIdx]); }
    else if (e.key === "Escape") { setOpen(false); inputRef.current?.blur(); }
  };

  const showDropdown = open && (q.trim().length >= 2);

  return (
    <div ref={rootRef} className="relative w-full max-w-[420px]" data-testid="global-search-root">
      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
        <input
          ref={inputRef}
          data-testid="global-search-input"
          type="text"
          value={q}
          placeholder="Search  (Ctrl+K)"
          onFocus={() => setOpen(true)}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={onKey}
          className="w-full h-9 pl-9 pr-8 text-sm rounded-md bg-white/70 border border-brand-navy/15 focus:border-brand-navy/40 focus:bg-white focus:outline-none text-brand-navy placeholder:text-zinc-400 font-mono tracking-tight"
        />
        {loading && (
          <Loader2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 animate-spin" />
        )}
      </div>

      {showDropdown && (
        <div
          data-testid="global-search-dropdown"
          className="absolute z-[60] left-0 right-0 mt-1 bg-white rounded-md border border-zinc-200 shadow-lg overflow-hidden"
        >
          {hits.length === 0 && !loading && (
            <div className="px-3 py-4 text-xs text-zinc-500 text-center font-mono">
              No result for "<span className="text-brand-navy">{q}</span>"
            </div>
          )}
          {hits.length === 0 && loading && (
            <div className="px-3 py-4 text-xs text-zinc-400 text-center font-mono">Searching…</div>
          )}
          {hits.map((hit, idx) => {
            const Icon = KIND_ICON[hit.kind] || Compass;
            const active = idx === activeIdx;
            return (
              <button
                key={`${hit.kind}-${hit.link}-${idx}`}
                data-testid={`global-search-hit-${idx}`}
                onMouseEnter={() => setActiveIdx(idx)}
                onClick={() => pick(hit)}
                className={`w-full flex items-center gap-3 px-3 py-2 text-left border-b border-zinc-100 last:border-b-0 transition-colors ${
                  active ? "bg-brand-cream/70" : "hover:bg-zinc-50"
                }`}
              >
                <span className={`shrink-0 w-7 h-7 rounded-md flex items-center justify-center ${
                  active ? "bg-brand-navy text-white" : "bg-zinc-100 text-zinc-600"
                }`}>
                  <Icon size={14} />
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block text-sm text-brand-navy font-medium truncate">{hit.label}</span>
                  <span className="block text-[10px] font-mono uppercase tracking-wide text-zinc-500 truncate">
                    {KIND_LABEL[hit.kind] || hit.kind}{hit.sub ? ` · ${hit.sub}` : ""}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
